/**
 * PasswordUpdateRequired — the forced stop when an existing password no longer
 * meets the strength policy.
 *
 * Reached only via AuthGate, which redirects here and refuses to render any
 * other staff route while `user.password_update_required` is true. There is no
 * dismiss and no skip — the only ways out are setting a compliant password or
 * signing out.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, LogOut } from "lucide-react";
import { auth as authApi, request } from "../lib/api";
import { useUser } from "../lib/auth";
import { PasswordRequirements, passwordOk } from "../components/PasswordRequirements";

export default function PasswordUpdateRequiredPage() {
  const nav = useNavigate();
  const { user, refresh } = useUser() as any;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = passwordOk(next) && next === confirm && current.length > 0 && next !== current;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      await request<{ ok: true }>("/api/auth/change-password", {
        method: "POST",
        body: { current_password: current, new_password: next },
      });
      if (typeof refresh === "function") await refresh();
      nav("/dashboard", { replace: true });
      // Belt and braces: if the context didn't refresh, a reload picks up the
      // cleared flag from /api/auth/me.
      setTimeout(() => window.location.reload(), 50);
    } catch (err: any) {
      setError(err?.message || "Could not update your password.");
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="panel" style={{ maxWidth: 460, width: "100%" }}>
        <div className="panel-header">
          <h2 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <ShieldAlert size={18} aria-hidden="true" /> Update your password
          </h2>
        </div>

        <p className="muted" style={{ marginTop: 0 }}>
          We strengthened the password requirements to protect client credit files.
          Your current password{user?.name ? `, ${user.name},` : ""} doesn&rsquo;t meet
          them yet — please set a new one to continue.
        </p>

        <form onSubmit={submit} className="stacked-form">
          <label className="form-field">
            <span>Current password</span>
            <input type="password" value={current} autoComplete="current-password"
                   onChange={(e) => { setCurrent(e.target.value); setError(null); }} required />
          </label>
          <label className="form-field">
            <span>New password</span>
            <input type="password" value={next} autoComplete="new-password"
                   onChange={(e) => { setNext(e.target.value); setError(null); }} required />
            <PasswordRequirements password={next} />
          </label>
          <label className="form-field">
            <span>Confirm new password</span>
            <input type="password" value={confirm} autoComplete="new-password"
                   onChange={(e) => { setConfirm(e.target.value); setError(null); }} required />
            {confirm.length > 0 && next !== confirm && (
              <span className="error-text">Passwords don&rsquo;t match.</span>
            )}
          </label>

          {next.length > 0 && next === current && (
            <span className="error-text">Your new password must be different from the current one.</span>
          )}
          {error && <p className="error-text" role="alert">{error}</p>}

          <div className="form-actions" style={{ alignItems: "center", gap: 12 }}>
            <button className="btn-primary" disabled={busy || !ready}>
              {busy ? "Saving…" : "Update password"}
            </button>
            <button type="button" className="btn-secondary"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                    onClick={async () => { await authApi.logout(); nav("/login", { replace: true }); }}>
              <LogOut size={14} aria-hidden="true" /> Sign out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
